import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Category as CategoryIcon,
  QrCode as QrCodeIcon,
  LocalOffer as LocalOfferIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Store as StoreIcon,
  BrandingWatermark as BrandingWatermarkIcon
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';
import { deleteProduct } from '../services/productService';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Try to get product data from navigation state first
  const navigationData = location.state?.productData;
  
  const [product, setProduct] = useState(navigationData || null);
  const [loading, setLoading] = useState(!navigationData);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);

  // Mock analytics data (replace with real API calls)
  const [salesData] = useState([
    { month: 'Jan', sales: 65, revenue: 1950 },
    { month: 'Feb', sales: 59, revenue: 1770 },
    { month: 'Mar', sales: 80, revenue: 2400 },
    { month: 'Apr', sales: 81, revenue: 2430 },
    { month: 'May', sales: 56, revenue: 1680 },
    { month: 'Jun', sales: 55, revenue: 1650 }
  ]);

  const [purchaseData] = useState([
    { month: 'Jan', purchases: 45, cost: 675 },
    { month: 'Feb', purchases: 52, cost: 780 },
    { month: 'Mar', purchases: 48, cost: 720 },
    { month: 'Apr', purchases: 61, cost: 915 },
    { month: 'May', purchases: 55, cost: 825 },
    { month: 'Jun', purchases: 67, cost: 1005 }
  ]);

  const [stockMovement] = useState([
    { date: '2024-01', inbound: 100, outbound: 45, balance: 155 },
    { date: '2024-02', inbound: 50, outbound: 52, balance: 153 },
    { date: '2024-03', inbound: 75, outbound: 48, balance: 180 },
    { date: '2024-04', inbound: 60, outbound: 61, balance: 179 },
    { date: '2024-05', inbound: 80, outbound: 55, balance: 204 },
    { date: '2024-06', inbound: 40, outbound: 67, balance: 177 }
  ]);

  const [performanceMetrics] = useState([
    { name: 'Total Sales', value: 396, color: '#8884d8' },
    { name: 'Returns', value: 12, color: '#82ca9d' },
    { name: 'Exchanges', value: 8, color: '#ffc658' },
    { name: 'Damages', value: 3, color: '#ff7300' }
  ]);

  // ✅ Load product data - Fixed to always fetch if no navigation data
  useEffect(() => {
    if (!navigationData && id) {
      loadProductFromAPI();
    }
  }, [id, navigationData]);

  // Load analytics data
  useEffect(() => {
    if (id) {
      loadProductAnalytics();
    }
  }, [id]);

  const loadProductFromAPI = async () => {
    try {
      setLoading(true);
      console.log('📤 Fetching product with ID:', id);
      
      const response = await axiosInstance.get(`Products/${id}`);
      console.log('✅ Product API Response:', response.data);
      
      if (response.data.status === 'Success' && response.data.payload) {
        setProduct(response.data.payload);
        console.log('✅ Product loaded:', response.data.payload);
      } else if (response.data) {
        // Handle case where response doesn't have status/payload structure
        setProduct(response.data);
        console.log('✅ Product loaded (alternative structure):', response.data);
      } else {
        throw new Error('Product not found');
      }
    } catch (error) {
      console.error('❌ Error loading product:', error);
      toast.error('Failed to load product details');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const loadProductAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      // Replace with actual API call
      // const response = await axiosInstance.get(`Products/${id}/analytics`);
      
      // Mock analytics data
      setTimeout(() => {
        setAnalytics({
          totalSales: 396,
          totalRevenue: 11880,
          averageOrderValue: 30,
          stockTurnover: 4.2,
          profitMargin: 45.5,
          lastSaleDate: '2024-06-15',
          topCustomers: [
            { name: 'John Doe', orders: 15, total: 450 },
            { name: 'Jane Smith', orders: 12, total: 360 },
            { name: 'Bob Johnson', orders: 8, total: 240 }
          ]
        });
        setAnalyticsLoading(false);
      }, 1000);
    } catch (error) {
      console.error('❌ Error loading analytics:', error);
      setAnalyticsLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/products/edit/${id}`, {
      state: { productData: product }
    });
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete "${product?.Name}"?`)) {
      try {
        await deleteProduct(id);
        toast.success('Product deleted successfully');
        navigate('/products');
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  const TabPanel = ({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`product-tabpanel-${index}`}
      aria-labelledby={`product-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Product not found</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/products')}
          sx={{ mt: 2 }}
        >
          Back to Products
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/products')} sx={{ color: 'primary.main' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {product.Name || product.ProductName || 'Product Details'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              SKU: {product.SKU || 'N/A'} • Product Details
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{ borderRadius: 2 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            sx={{ borderRadius: 2 }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Product Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    €{Number(product.SalePrice || product.Price || 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sale Price
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 48, height: 48 }}>
                  <InventoryIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {product.StockQuanity || product.StockQuantity || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Stock Quantity
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: analytics ? 'info.main' : 'grey.400', width: 48, height: 48 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {analyticsLoading ? '...' : analytics?.totalSales || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Sales
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: analytics ? 'warning.main' : 'grey.400', width: 48, height: 48 }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    €{analyticsLoading ? '...' : (analytics?.totalRevenue || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Revenue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for Product Details and Analytics */}
      <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={(e, newValue) => setCurrentTab(newValue)}
            sx={{ px: 3 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Product Info" />
            <Tab label="Sales Analytics" />
            <Tab label="Stock Movement" />
            <Tab label="Performance" />
          </Tabs>
        </Box>

        {/* Product Details Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Basic Information
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemIcon><CategoryIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Category" 
                    secondary={
                      product.CategoryName || 
                      product.Category?.CategoryName || 
                      product.ProductCategory?.CategoryName || 
                      product.category?.name || 
                      'No Category'
                    } 
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon><QrCodeIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Barcode" 
                    secondary={product.Barcode || product.BarCode || 'No Barcode'}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon><BusinessIcon color="primary" /></ListItemIcon>
                  <ListItemText 
                    primary="Supplier" 
                    secondary={
                      product.SupplierName || 
                      product.Supplier?.Name || 
                      product.supplier?.name || 
                      'No Supplier'
                    } 
                  />
                </ListItem>

                {product.BrandName && (
                  <ListItem>
                    <ListItemIcon><BrandingWatermarkIcon color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Brand" 
                      secondary={product.BrandName || 'No Brand'} 
                    />
                  </ListItem>
                )}

                {product.ModelNumber && (
                  <ListItem>
                    <ListItemIcon><StoreIcon color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="Model Number" 
                      secondary={product.ModelNumber} 
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemIcon>
                    {product.IsActive ? 
                      <CheckCircleIcon color="success" /> : 
                      <WarningIcon color="error" />
                    }
                  </ListItemIcon>
                  <ListItemText 
                    primary="Status" 
                    secondary={
                      <Chip 
                        label={product.IsActive ? 'Active' : 'Inactive'} 
                        color={product.IsActive ? 'success' : 'error'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    } 
                  />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Pricing and Stock
              </Typography>
              
              <List>
                <ListItem>
                  <ListItemText 
                    primary="Cost Price" 
                    secondary={`€${Number(product.CostPrice || 0).toFixed(2)}`}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary="Sale Price" 
                    secondary={`€${Number(product.SalePrice || product.Price || 0).toFixed(2)}`}
                  />
                </ListItem>

                {product.MinSalePrice > 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="Min Sale Price" 
                      secondary={`€${Number(product.MinSalePrice || 0).toFixed(2)}`}
                    />
                  </ListItem>
                )}

                {product.AveragePrice > 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="Average Price" 
                      secondary={`€${Number(product.AveragePrice || 0).toFixed(2)}`}
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemText 
                    primary="Current Stock" 
                    secondary={product.StockQuanity || product.StockQuantity || 0}
                  />
                </ListItem>

                <ListItem>
                  <ListItemText 
                    primary="Min Stock Level" 
                    secondary={product.MinStockLevel || 0}
                  />
                </ListItem>
              </List>

              {product.IsPromotionProduct && (
                <Box sx={{ mt: 2 }}>
                  <Chip 
                    icon={<LocalOfferIcon />}
                    label={`${product.PromotionPercentage || 0}% OFF - Promotion Active`}
                    color="error"
                    variant="filled"
                  />
                </Box>
              )}
            </Grid>

            {product.Description && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Description
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {product.Description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Sales Analytics Tab */}
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Sales Trend (Last 6 Months)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="sales" stroke="#8884d8" strokeWidth={2} name="Units Sold" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Revenue Trend (Last 6 Months)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={salesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`€${value}`, 'Revenue']} />
                  <Area type="monotone" dataKey="revenue" stroke="#82ca9d" fill="#82ca9d" />
                </AreaChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Purchase vs Sales Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={salesData.map((sale, index) => ({
                  ...sale,
                  purchases: purchaseData[index]?.purchases || 0
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="sales" fill="#8884d8" name="Sales" />
                  <Bar dataKey="purchases" fill="#82ca9d" name="Purchases" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Stock Movement Tab */}
        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Stock Movement History
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={stockMovement}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="inbound" stroke="#8884d8" strokeWidth={2} name="Stock In" />
                  <Line type="monotone" dataKey="outbound" stroke="#82ca9d" strokeWidth={2} name="Stock Out" />
                  <Line type="monotone" dataKey="balance" stroke="#ffc658" strokeWidth={3} name="Balance" />
                </LineChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Tab */}
        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Product Performance Breakdown
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={performanceMetrics}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {performanceMetrics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Key Metrics
              </Typography>
              {analyticsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Average Order Value" 
                      secondary={`€${analytics?.averageOrderValue || 0}`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Stock Turnover Rate" 
                      secondary={`${analytics?.stockTurnover || 0}x per year`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Profit Margin" 
                      secondary={`${analytics?.profitMargin || 0}%`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText 
                      primary="Last Sale Date" 
                      secondary={analytics?.lastSaleDate || 'N/A'}
                    />
                  </ListItem>
                </List>
              )}
            </Grid>

            {analytics?.topCustomers && analytics.topCustomers.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Top Customers
                </Typography>
                <List>
                  {analytics.topCustomers.map((customer, index) => (
                    <ListItem key={index}>
                      <ListItemText 
                        primary={customer.name}
                        secondary={`${customer.orders} orders • €${customer.total} total`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default ProductDetailPage;